import { SkeletonEvent, AssetType } from '../../types.js';
import { preloadScript, loadScript, loadStylesheet } from '../scripts.js';

var LoadFilesMode;
(function (LoadFilesMode) {
    LoadFilesMode[LoadFilesMode["preFetchOnly"] = 0] = "preFetchOnly";
    LoadFilesMode[LoadFilesMode["skipPreFetch"] = 1] = "skipPreFetch";
    LoadFilesMode[LoadFilesMode["withPreFetch"] = 2] = "withPreFetch";
})(LoadFilesMode || (LoadFilesMode = {}));
class ManifestHandler {
    constructor({ emitSkeletonEvent, sharedDependencies, }) {
        this.emitSkeletonEvent = emitSkeletonEvent;
        this.sharedDependencies = sharedDependencies;
    }
    async loadMfAssets({ mfName, manifest, assetsDir, mode = LoadFilesMode.withPreFetch, appendScript, }) {
        const sharedDependenciesBuckets = this.getSharedDependenciesBuckets({ manifest });
        const mfAssets = manifest.entrypoints.map(entry => `${assetsDir}/${entry}`);
        return this.loadAssets({ mfName, mode, sharedDependenciesBuckets, mfAssets, appendScript });
    }
    async batchLoadMfsAssets({ mfNames, mfAssets, sharedDependenciesBuckets, mode = LoadFilesMode.withPreFetch, }) {
        return this.loadAssets({ mfName: mfNames.join(','), mode, sharedDependenciesBuckets, mfAssets });
    }
    async loadAssets({ mfName, mfAssets, sharedDependenciesBuckets, mode = LoadFilesMode.withPreFetch, appendScript, }) {
        if (mode !== LoadFilesMode.skipPreFetch) {
            // start prefetching in the background, so that when we actually need the script it will be partially downloaded
            const sharedDependenciesToPreload = sharedDependenciesBuckets
                .flat()
                .filter(dep => !this.sharedDependencies.getDependencyLoadPromise(dep.globalName));
            this.preload({
                mfAssets,
                sharedDependenciesBuckets: [sharedDependenciesToPreload],
                mfName,
                appendScript,
            });
        }
        if (mode === LoadFilesMode.preFetchOnly) {
            return { assetsLoadTime: 0 };
        }
        await this.loadSharedDependencies({
            mfName,
            sharedDependenciesBuckets,
        });
        const assetsLoadTime = await this.loadManifestAssets({ mfAssets, mfName, appendScript });
        return { assetsLoadTime };
    }
    getSharedDependenciesBuckets({ manifest }) {
        const { shared, sameAsHost } = manifest || {};
        // If this MF doesn't support shared dependencies, we don't need to do anything
        if (!shared?.length) {
            return [];
        }
        // This MF asks to forcefully use the dependencies of the host. We won't load its shared dependencies.
        if (sameAsHost === true) {
            return [];
        }
        const buckets = [];
        shared.forEach(({ globalName, dependencies }) => {
            if (!globalName) {
                throw new Error(`Attempted to load a shared dependency with missing globalName".\
        This most likely means that the MF has an old Trident version which is no longer supported`);
            }
            const url = this.sharedDependencies.getUrl(globalName);
            const numOfDependencies = Object.keys(dependencies).length;
            buckets[numOfDependencies] || (buckets[numOfDependencies] = []);
            buckets[numOfDependencies].push({ globalName: globalName, url });
        });
        return buckets;
    }
    async loadSharedDependencies({ sharedDependenciesBuckets, mfName, appendScript, }) {
        const startLoadTime = Date.now();
        this.emitSkeletonEvent(SkeletonEvent.SharedDependenciesLoadStart, { mfName });
        for (let i = 0; i < sharedDependenciesBuckets.length; i++) {
            const depsToLoadNext = sharedDependenciesBuckets[i] || [];
            const loadAllNextSharedDeps = Promise.all(depsToLoadNext.map(dep => this.sharedDependencies.ensureDependencyLoadPromise(dep.globalName, () => this.loadAsset({ mfName, src: dep.url, appendScript }))));
            await loadAllNextSharedDeps; // wait for the current bucket to finish loading before start loading the next
        }
        this.emitSkeletonEvent(SkeletonEvent.SharedDependenciesLoadEnd, { mfName, loadTime: Date.now() - startLoadTime });
    }
    preload({ mfAssets, sharedDependenciesBuckets, mfName, appendScript, }) {
        const allSharedDependencies = sharedDependenciesBuckets.flat();
        const sharedDependenciesURLs = allSharedDependencies.map(({ url }) => url);
        const allJSAssets = [...sharedDependenciesURLs, ...mfAssets].filter(url => url.endsWith('.js'));
        const cssAssets = mfAssets.filter(url => url.endsWith('.css'));
        Promise.all([
            ...allJSAssets.map(url => preloadScript({ mfName, src: url, appendScript })),
            ...cssAssets.map(url => preloadScript({ mfName, src: url, assetType: AssetType.css })),
        ]).catch(e => {
            throw e;
        });
    }
    async loadAsset({ src, mfName, appendScript }) {
        if (src.endsWith('.js')) {
            await loadScript({ mfName, src, onEvent: this.emitSkeletonEvent, appendScript });
        }
        else if (src.endsWith('.css')) {
            await loadStylesheet({ mfName, src, onEvent: this.emitSkeletonEvent });
        }
    }
    async loadManifestAssets({ mfAssets, mfName, appendScript, }) {
        this.emitSkeletonEvent(SkeletonEvent.StartLoadingJsAssets, {
            mfName,
        });
        const loadingAssetsStartTime = Date.now();
        const cssFilesLoadingPromises = [];
        const { cssFiles, jsFiles } = mfAssets.reduce((acc, fileName) => {
            if (fileName.endsWith('.js')) {
                acc.jsFiles.push(fileName);
            }
            else if (fileName.endsWith('.css')) {
                acc.cssFiles.push(fileName);
            }
            return acc;
        }, { cssFiles: [], jsFiles: [] });
        cssFiles.forEach(src => {
            cssFilesLoadingPromises.push(this.loadAsset({ mfName, src }));
        });
        for (const src of jsFiles) {
            await this.loadAsset({ mfName, src, appendScript });
        }
        this.emitSkeletonEvent(SkeletonEvent.SuccessLoadingJsAssets, { mfName });
        await Promise.all(cssFilesLoadingPromises);
        const totalTime = Date.now() - loadingAssetsStartTime;
        return totalTime;
    }
}

export { LoadFilesMode, ManifestHandler };
